import { Check, Close, Delete } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from "@mui/material";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import PCheckbox from "components/PCheckbox";
import PSelect from "components/PSelect";
import { CustomPropertyQuery, FieldPresence } from "generated/graphql";
import { useMemo, useState } from "react";
import theme from "theme/index";
import { P } from "theme/typography";

const EXACT_OPTIONS = [
  { label: "Exact", value: true },
  { label: "Partial", value: undefined },
];

type NonNullify<T> = Omit<
  {
    [K in keyof T]: Exclude<T[K], null> | undefined;
  },
  "__typename"
>;

type CustomPropertyQueryState = NonNullify<CustomPropertyQuery> & Pick<CustomPropertyQuery, "key">;

interface CustomPropertyQueryFormProps {
  customPropertyQuery?: CustomPropertyQuery;
  onDelete?: () => void;
  onChange: (query: CustomPropertyQuery) => void;
}

const CustomPropertyQueryForm = ({ customPropertyQuery, onDelete, onChange }: CustomPropertyQueryFormProps) => {
  const initialValues: CustomPropertyQueryState = {
    key: customPropertyQuery?.key || "",
    value: customPropertyQuery?.value ?? undefined,
    exact: customPropertyQuery?.exact ?? undefined,
    presence: customPropertyQuery?.presence ?? undefined,
    excludeValue: customPropertyQuery?.excludeValue || undefined,
  };

  const [propertyValues, setPropertyValues] = useState<CustomPropertyQueryState>(initialValues);
  const [testValue, setTestValue] = useState<string>("");

  function handleChange<T extends keyof CustomPropertyQueryState>(key: T, value: CustomPropertyQueryState[T]) {
    const newPropertyValues: CustomPropertyQueryState = {
      ...propertyValues,
      [key]: value,
    };
    setPropertyValues(newPropertyValues);
    onChange(newPropertyValues);
  }

  const runPropertyQuery = ({ testValue, query }: { testValue: string; query: CustomPropertyQueryState }) => {
    if (query.presence === undefined) {
      let matching: boolean;
      if (query.exact === true) {
        matching = query.value === testValue;
      } else {
        if (!query.value) matching = false;
        else {
          matching = testValue.includes(query.value);
        }
      }
      return query.excludeValue ? !matching : matching;
    } else {
      return query.presence === FieldPresence.NonNull ? testValue !== "" : testValue === "";
    }
  };

  const testValueIsMatchingMemoized = useMemo(
    () => runPropertyQuery({ testValue, query: propertyValues }),
    [testValue, propertyValues]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        border: 1,
        borderColor: theme.palette.grey[100],
        borderRadius: 1,
      }}>
      <Box
        sx={{
          p: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}>
        <FormControl>
          <TextField
            label="Key"
            name="key"
            value={propertyValues.key}
            onChange={(e) => handleChange<"key">("key", e.target.value)}
          />
        </FormControl>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}>
          <P variant="xsBold">Query Type:</P>
          <FormControl>
            <RadioGroup
              row
              value={propertyValues.presence ? "presence" : "value"}
              onChange={(e) =>
                handleChange<"presence">("presence", e.target.value === "presence" ? FieldPresence.NonNull : undefined)
              }>
              <FormControlLabel value="value" control={<Radio />} label="Value" />
              <FormControlLabel value="presence" control={<Radio />} label="Presence" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>
      <Box
        sx={{
          px: 1,
        }}>
        {propertyValues.presence !== undefined ? (
          <FormControl>
            <FormLabel>Presence</FormLabel>
            <PSelect
              name="presence"
              fullWidth
              onChange={(e) => handleChange<"presence">("presence", e.target.value as FieldPresence)}
              value={propertyValues.presence}>
              <MenuItem value="NonNull">NonNull</MenuItem>
              <MenuItem value="Null">Null</MenuItem>
            </PSelect>
          </FormControl>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}>
            <FormControl
              sx={{
                minWidth: "100px",
              }}>
              <PSelect
                name="exact"
                fullWidth
                onChange={(e) => {
                  const selectedOption = EXACT_OPTIONS.find((option) => option.label === e.target.value);
                  handleChange<"exact">("exact", selectedOption ? selectedOption.value : false);
                }}
                value={EXACT_OPTIONS.find((option) => option.value === propertyValues.exact)?.label || "Partial"}>
                {EXACT_OPTIONS.map((option) => (
                  <MenuItem key={option.label} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </PSelect>
            </FormControl>
            <TextField
              fullWidth
              label="Value"
              name="value"
              value={propertyValues.value}
              onChange={(e) => {
                handleChange<"value">("value", e.target.value);
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}>
              <PCheckbox
                label="Exclude"
                name="excludeValue"
                checked={propertyValues.excludeValue === true}
                onChange={(e) => handleChange<"excludeValue">("excludeValue", e.target.checked)}
              />
            </Box>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          backgroundColor: theme.palette.grey[50],
          p: 1,
          borderRadius: 1,
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            gap: 1,
          }}>
          <TextField
            fullWidth
            label="Test Property Value"
            value={testValue}
            onChange={(e) => {
              setTestValue(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <Tooltip title={testValueIsMatchingMemoized ? "Matching" : "Not Matching"}>
                  {testValueIsMatchingMemoized ? <Check color="success" /> : <Close color="error" />}
                </Tooltip>
              ),
            }}
          />
          <DescriptionTooltip description="Enter a test value to see if it matches the query." />
        </Box>
        <div>
          <Tooltip title="Delete">
            <IconButton onClick={onDelete}>
              <Delete />
            </IconButton>
          </Tooltip>
        </div>
      </Box>
    </Box>
  );
};

export default CustomPropertyQueryForm;
